// Data access layer for Guides. Supabase-only implementation.
import { PostgrestError } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabase';
import { Guide, GuideStatus, GuideBasicInfo, GuideProfessionalExperience, GuideLegacyProfessional, GuideReference, GuideBackgroundCheck, GuidePackage, GuidePackageComponent, GuideOnboardingData, GuideClientCapacity } from '../types/guide';

export type ListParams = {
    /** Free-text search against name/email */
    q?: string;
    /** Filter by status (Pending / Consult Required / Approved / Not a Fit) */
    status?: GuideStatus | "all";
    /** Income dollars range */
    incomeMin?: number;
    incomeMax?: number;
    /** Simple client-side paging for now */
    page?: number;
    pageSize?: number;
    /** Sort field */
    sortBy?: "name" | "status" | "connectedSeekers" | "income" | "joinedAt";
    /** Sort direction */
    sortDirection?: "asc" | "desc";
};

export type ListResult = {
    items: Guide[];
    total: number;
};

type SingleResponse<T> = { data: T | null; error: PostgrestError | null };
type ListResponse<T> = { data: T[] | null; error: PostgrestError | null; count?: number | null };

function getEnv(): any {
    return (window as any).__ENV__;
}

function isNotFound(error: PostgrestError | null): boolean {
    return !!error && error.code === "PGRST116";
}

function unwrapSingle<T>(response: SingleResponse<T>): T | null {
    if (response.error) {
        if (isNotFound(response.error)) return null;
        throw response.error;
    }
    return response.data ?? null;
}

function unwrapList<T>(response: ListResponse<T>): T[] {
    if (response.error) {
        if (isNotFound(response.error)) return [];
        throw response.error;
    }
    return response.data ?? [];
}

function normalizeBoolean(value: unknown): boolean | null {
    if (typeof value === "boolean") return value;
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim().toLowerCase();
    if (["true", "yes", "1"].includes(normalized)) return true;
    if (["false", "no", "0"].includes(normalized)) return false;
    return null;
}

function toStringArray(input: unknown): string[] {
    if (!Array.isArray(input)) return [];
    return input
        .map((item) => (typeof item === "string" ? item.trim() : null))
        .filter((item): item is string => !!item && item.length > 0);
}

function toNumberOrNull(value: unknown): number | null {
    if (typeof value === "number" && !Number.isNaN(value)) return value;
    if (value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
}

function getSB() {
    return getSupabaseClient();
}

export async function listGuides(params: ListParams = {}): Promise<ListResult> {
    const sb = getSB();
    const { q, status = "all", incomeMin, incomeMax, page = 1, pageSize = 10, sortBy = "joinedAt", sortDirection = "desc" } = params;

    let query = sb
        .from("admin_guides_view")
        .select("*", { count: "exact" });

    if (q && q.trim()) {
        const s = q.trim();
        query = query.or(`name.ilike.%${s}%,email.ilike.%${s}%`);
    }

    if (status !== "all") {
        query = query.eq("status", status);
    }
    if (typeof incomeMin === "number") {
        query = query.gte("income", incomeMin);
    }
    if (typeof incomeMax === "number") {
        query = query.lte("income", incomeMax);
    }

    // Apply sorting
    const columnMap: Record<typeof sortBy, string> = {
        name: "name",
        status: "status",
        connectedSeekers: "connected_seekers",
        income: "income",
        joinedAt: "joined_at"
    };
    const column = columnMap[sortBy];
    query = query.order(column, { ascending: sortDirection === "asc" });

    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;
    const { data, error, count } = await query.range(start, end);
    if (error) throw error;

    const items: Guide[] = (data ?? []).map((row: any) => {
        const rawId = row.user_id ?? row.id;
        const id =
            typeof rawId === "string"
                ? rawId
                : rawId !== undefined && rawId !== null
                ? String(rawId)
                : "";
        return {
            id,
            name: row.name ?? "",
            email: row.email ?? "",
            phone: row.phone ?? null,
            status: (row.status ?? "pending") as GuideStatus,
            backgroundChecked: normalizeBoolean(row.backgroundChecked),
            connectedSeekers: Number(row.connected_seekers ?? 0),
            income: Number(row.income ?? 0),
            joinedAt: row.joined_at ?? new Date().toISOString(),
            region: row.location ?? row.region ?? undefined,
            specialties: Array.isArray(row.specialties) ? row.specialties : undefined,
        };
    });

    return { items, total: count ?? items.length };
}

export async function updateGuideStatus(id: string, next: GuideStatus): Promise<void> {
    const sb = getSB();
    const record = await fetchGuideRow(sb, id);
    const targetId = record?.user_id ?? record?.id ?? id;

    const response = await sb
        .from("guides")
        .update({ status: next })
        .eq("user_id", targetId)
        .select("user_id");

    if (response.error) throw response.error;
    if (!response.data || response.data.length === 0) {
        throw new Error(`Guide ${id} not found when updating status.`);
    }
}

// Small helper to simulate latency
function sleep(ms: number) {
    return new Promise((res) => setTimeout(res, ms));
}

async function fetchBasicInfo(
    sb: ReturnType<typeof getSB>,
    userId: string
): Promise<SingleResponse<any>> {
    const response = await sb.from("basic_info").select("*").eq("user_id", userId).maybeSingle();
    if (response.error && !isNotFound(response.error)) {
        throw response.error;
    }
    return response as SingleResponse<any>;
}
async function loadOnboardingData(
    sb: ReturnType<typeof getSB>,
    identifiers: { guideId?: string | null; userId?: string | null }
): Promise<GuideOnboardingData> {
    const guideId = identifiers.guideId ?? identifiers.userId ?? null;
    const userId = identifiers.userId ?? identifiers.guideId ?? null;

    if (!guideId && !userId) {
        return {};
    }

    const [
        basicInfoRes,
        professionalExperienceRes,
        legacyProfessionalRes,
        legacyExperienceTypesRes,
        legacyPlacesRes,
        enhancedProfileRes,
        clientCapacityRes,
        referencesRes,
        backgroundChecksRes,
        packagesRes,
    ] = await Promise.all([
        userId ? fetchBasicInfo(sb, userId) : Promise.resolve({ data: null, error: null }),
        guideId
            ? sb
                  .from("guide_professional_experience")
                  .select("*")
                  .eq("guide_id", guideId)
                  .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        guideId
            ? sb
                  .from("guide_professional")
                  .select("years_practicing, employment_type, is_main_income, ceremonies_count, has_medical_director, works_with_other_facilitators, gender_id, personal_relationship, modality, created_at, updated_at")
                  .eq("guide_id", guideId)
                  .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        // guide_experience_types legacy table access disabled – return stub data
        Promise.resolve({ data: [], error: null }),
        // guide_places legacy table access disabled – return stub data
        Promise.resolve({ data: [], error: null }),
        guideId
            ? sb
                  .from("guides_enhanced_profile")
                  .select("*")
                  .eq("guide_id", guideId)
                  .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        guideId
            ? sb
                  .from("guide_client_capacity")
                  .select("consultations_per_week, new_clients_per_month, platform_commitment, service_fee_acceptance, created_at, updated_at")
                  .eq("guide_id", guideId)
                  .maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        userId
            ? sb
                  .from("guide_references")
                  .select("*")
                  .eq("user_id", userId)
            : Promise.resolve({ data: [], error: null }),
        userId
            ? sb
                  .from("guide_background_checks")
                  .select("*")
                  .eq("user_id", userId)
                  .order("submitted_at", { ascending: false })
                  : Promise.resolve({ data: [], error: null }),
        guideId
            ? sb
                  .from("guide_packages")
                  .select("*")
                  .eq("guide_id", guideId)
            : Promise.resolve({ data: [], error: null }),
    ]);

    const basicRow = unwrapSingle<any>(basicInfoRes as SingleResponse<any>);
    const experienceRow = unwrapSingle<any>(
        professionalExperienceRes as SingleResponse<any>
    );
    const legacyRow = unwrapSingle<any>(
        legacyProfessionalRes as SingleResponse<any>
    );
    const legacyTypesRows = unwrapList<{ experience_type_id: number }>(legacyExperienceTypesRes as ListResponse<any>);
    const legacyPlacesRows = unwrapList<{ place: string | null }>(legacyPlacesRes as ListResponse<any>);
    const enhancedProfileRow = unwrapSingle<{
        about?: string | null;
        image_url?: string | null;
        created_at?: string | null;
        updated_at?: string | null;
    }>(enhancedProfileRes as SingleResponse<any>);
    const clientCapacityRow = unwrapSingle<{
        consultations_per_week?: string | null;
        new_clients_per_month?: string | null;
        platform_commitment?: string | null;
        service_fee_acceptance?: string | null;
        created_at?: string | null;
        updated_at?: string | null;
    }>(clientCapacityRes as SingleResponse<any>);
    const referencesRows = unwrapList<GuideReference>(referencesRes as ListResponse<any>);
    const backgroundCheckRows = unwrapList<GuideBackgroundCheck>(backgroundChecksRes as ListResponse<any>);
    const packageRows = unwrapList<GuidePackage>(packagesRes as ListResponse<any>);

    const basicInfo: GuideBasicInfo | undefined = basicRow
        ? {
              userId: basicRow.user_id ?? basicRow.id ?? userId ?? undefined,
              location: basicRow.location ?? null,
              bio: basicRow.bio ?? null,
              onboardingStep: toNumberOrNull(basicRow.onboarding_step),
              createdAt: basicRow.created_at ?? null,
              updatedAt: basicRow.updated_at ?? null,
          }
        : undefined;

    const professionalExperience: (GuideProfessionalExperience & Record<string, unknown>) | undefined =
        experienceRow
            ? (() => {
                  const {
                      guide_id,
                      experience_types,
                      legal_status,
                      support_types,
                      travel_willingness,
                      service_locations,
                      focus_areas,
                      excluded_areas,
                      works_with_medications,
                      special_groups,
                      religious_beliefs,
                      created_at,
                      updated_at,
                      ...rest
                  } = experienceRow as Record<string, any>;

                  return {
                      ...rest,
                      guideId: guide_id ?? rest.guideId ?? undefined,
                      experienceTypes: toStringArray(experience_types ?? rest.experienceTypes),
                      legalStatus: legal_status ?? rest.legalStatus ?? null,
                      supportTypes: toStringArray(support_types ?? rest.supportTypes),
                      travelWillingness: toStringArray(travel_willingness ?? rest.travelWillingness),
                      serviceLocations: toStringArray(service_locations ?? rest.serviceLocations),
                      focusAreas: toStringArray(focus_areas ?? rest.focusAreas),
                      excludedAreas: toStringArray(excluded_areas ?? rest.excludedAreas),
                      worksWithMedications: works_with_medications ?? rest.worksWithMedications ?? null,
                      specialGroups: toStringArray(special_groups ?? rest.specialGroups),
                      religiousBeliefs: toStringArray(religious_beliefs ?? rest.religiousBeliefs),
                      createdAt: created_at ?? rest.createdAt ?? null,
                      updatedAt: updated_at ?? rest.updatedAt ?? null,
                  } as GuideProfessionalExperience & Record<string, unknown>;
              })()
            : undefined;

    const legacyProfessional: GuideLegacyProfessional | undefined = legacyRow
        ? {
              yearsPracticing: legacyRow.years_practicing ?? null,
              employmentType: legacyRow.employment_type ?? null,
              isMainIncome: normalizeBoolean(legacyRow.is_main_income),
              ceremoniesCount: legacyRow.ceremonies_count ?? null,
              hasMedicalDirector: normalizeBoolean(legacyRow.has_medical_director),
              worksWithOtherFacilitators: normalizeBoolean(legacyRow.works_with_other_facilitators),
              genderId: legacyRow.gender_id ?? null,
              personalRelationship: legacyRow.personal_relationship ?? null,
              modality: legacyRow.modality ?? null,
              createdAt: legacyRow.created_at ?? null,
              updatedAt: legacyRow.updated_at ?? null,
          }
        : undefined;

    const legacyExperienceTypeIds = legacyTypesRows
        .map((row) => (typeof row.experience_type_id === "number" ? row.experience_type_id : null))
        .filter((id): id is number => id !== null);

    const legacyPlaces = legacyPlacesRows
        .map((row) => (typeof row.place === "string" ? row.place : null))
        .filter((place): place is string => !!place);

    const enhancedProfile = enhancedProfileRow
        ? {
              about: enhancedProfileRow.about ?? null,
              imageUrl: enhancedProfileRow.image_url ?? null,
              createdAt: enhancedProfileRow.created_at ?? null,
              updatedAt: enhancedProfileRow.updated_at ?? null,
          }
        : undefined;

    const clientCapacity: GuideClientCapacity | undefined = clientCapacityRow
        ? {
              consultationsPerWeek: clientCapacityRow.consultations_per_week ?? null,
              newClientsPerMonth: clientCapacityRow.new_clients_per_month ?? null,
              platformCommitment: clientCapacityRow.platform_commitment ?? null,
              serviceFeeAcceptance: clientCapacityRow.service_fee_acceptance ?? null,
              createdAt: clientCapacityRow.created_at ?? null,
              updatedAt: clientCapacityRow.updated_at ?? null,
          }
        : undefined;

    const references: GuideReference[] = referencesRows.map((ref: any) => ({
        id: ref.id ?? undefined,
        refType: ref.ref_type ?? ref.refType ?? ref.type ?? null,
        fullName: ref.full_name ?? ref.reference_name ?? ref.name ?? null,
        email: ref.email ?? ref.reference_email ?? null,
        phone: ref.phone ?? ref.reference_phone ?? null,
        relationshipDuration: ref.relationship_duration ?? ref.duration ?? null,
        servicesProvided: ref.services_provided ?? ref.services ?? null,
        company: ref.company ?? ref.organization ?? null,
        jobTitle: ref.job_title ?? ref.title ?? null,
        professionalRelationship: ref.professional_relationship ?? ref.relationship ?? null,
        durationOfRelationship: ref.duration_of_relationship ?? null,
        createdAt: ref.created_at ?? null,
        updatedAt: ref.updated_at ?? null,
    }));

    const backgroundChecks: GuideBackgroundCheck[] = backgroundCheckRows.map((row: any) => ({
        id: row.id ?? undefined,
        status: row.status ?? null,
        submittedAt: row.submitted_at ?? null,
        filePath: row.file_path ?? null,
        storageBucket: row.storage_bucket ?? null,
        storageObjectKey: row.storage_object_key ?? null,
        originalFilename: row.original_filename ?? null,
        createdAt: row.created_at ?? null,
        updatedAt: row.updated_at ?? null,
    }));

    let packages: GuidePackage[] | undefined;
    if (packageRows.length) {
        const packageIds = packageRows
            .map((pkg: any) => (typeof pkg.id === "string" ? pkg.id : pkg.id != null ? String(pkg.id) : null))
            .filter((pkgId): pkgId is string => !!pkgId);

        let componentRows: any[] = [];
        if (packageIds.length) {
            const componentsRes = await sb
                .from("guide_package_components")
                .select("id, package_id, component_type, name, quantity, unit_price_dollars, sort_order, created_at, updated_at")
                .in("package_id", packageIds);
            componentRows = unwrapList<any>(componentsRes as ListResponse<any>);
        }

        const componentsByPackage = new Map<string, GuidePackageComponent[]>();
        componentRows.forEach((row: any) => {
            const pkgId = typeof row.package_id === "string" ? row.package_id : row.package_id != null ? String(row.package_id) : undefined;
            if (!pkgId) return;
            const bucket = componentsByPackage.get(pkgId) ?? [];
            const component: GuidePackageComponent = {
                id: row.id ?? undefined,
                packageId: pkgId,
                componentType: row.component_type ?? null,
                name: row.name ?? null,
                quantity: toNumberOrNull(row.quantity),
                unitPriceDollars: toNumberOrNull(row.unit_price_dollars),
                sortOrder: toNumberOrNull(row.sort_order),
                createdAt: row.created_at ?? null,
                updatedAt: row.updated_at ?? null,
            };
            bucket.push(component);
            if (!componentsByPackage.has(pkgId)) {
                componentsByPackage.set(pkgId, bucket);
            }
        });

        packages = packageRows.map((pkg: any) => {
            const pkgId = typeof pkg.id === "string" ? pkg.id : pkg.id != null ? String(pkg.id) : "";
            const isActive = normalizeBoolean(pkg.is_active);
            const components = (componentsByPackage.get(pkgId) ?? []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            return {
                id: pkgId,
                name: pkg.name ?? null,
                durationMonths: toNumberOrNull(pkg.duration_months),
                description: pkg.description ?? null,
                isActive,
                createdAt: pkg.created_at ?? null,
                updatedAt: pkg.updated_at ?? null,
                components,
            };
        });
    }

    const onboarding: GuideOnboardingData = {
        basicInfo,
        professionalExperience,
        legacyProfessional,
        legacyExperienceTypeIds: legacyExperienceTypeIds.length ? legacyExperienceTypeIds : undefined,
        legacyPlaces: legacyPlaces.length ? legacyPlaces : undefined,
        enhancedProfile,
        clientCapacity,
        references: references.length ? references : undefined,
        backgroundChecks: backgroundChecks.length ? backgroundChecks : undefined,
        packages: packages && packages.length ? packages : undefined,
    };

    const hasData =
        onboarding.basicInfo ||
        onboarding.professionalExperience ||
        onboarding.legacyProfessional ||
        (onboarding.legacyExperienceTypeIds && onboarding.legacyExperienceTypeIds.length) ||
        (onboarding.legacyPlaces && onboarding.legacyPlaces.length) ||
        onboarding.enhancedProfile ||
        onboarding.clientCapacity ||
        (onboarding.references && onboarding.references.length) ||
        (onboarding.backgroundChecks && onboarding.backgroundChecks.length) ||
        (onboarding.packages && onboarding.packages.length);

    return hasData ? onboarding : {};
}

async function fetchGuideRow(sb: ReturnType<typeof getSB>, id: string) {
    const attempts: Array<{ column: "id" | "user_id"; value: string | number }> = [
        { column: "id", value: id },
        { column: "user_id", value: id },
    ];

    const numericId = Number(id);
    if (!Number.isNaN(numericId)) {
        attempts.push({ column: "id", value: numericId });
        attempts.push({ column: "user_id", value: numericId });
    }

    for (const attempt of attempts) {
        const response = await sb.from("admin_guides_view").select("*").eq(attempt.column, attempt.value).maybeSingle();
        if (response.error) {
            if (!isNotFound(response.error)) throw response.error;
        } else if (response.data) {
            return response.data;
        }
    }

    return null;
}

async function calculateDealsClosedRate(
    sb: ReturnType<typeof getSB>,
    guideId: string
): Promise<number | null> {
    try {
        // Count unique seekers with accepted package offers
        const { data: acceptedOffers, error: offersError } = await sb
            .from("package_offers")
            .select("seeker_id")
            .eq("guide_id", guideId)
            .eq("status", "accepted");

        if (offersError) {
            console.error("Error fetching accepted offers:", offersError);
            return null;
        }

        const uniqueSeekers = new Set(
            (acceptedOffers ?? [])
                .map((offer: any) => offer.seeker_id)
                .filter((id: any) => id != null)
        );
        const uniqueSeekersCount = uniqueSeekers.size;

        // Count consultation sessions
        const { count: consultationCount, error: sessionsError } = await sb
            .from("scheduled_sessions")
            .select("*", { count: "exact", head: true })
            .eq("guide_id", guideId)
            .eq("session_type", "consultation");

        if (sessionsError) {
            console.error("Error fetching consultation sessions:", sessionsError);
            return null;
        }

        const totalConsultations = consultationCount ?? 0;

        // Calculate ratio (avoid division by zero)
        if (totalConsultations === 0) {
            return null;
        }

        return uniqueSeekersCount / totalConsultations;
    } catch (error) {
        console.error("Error calculating deals closed rate:", error);
        return null;
    }
}

export async function getGuide(id: string): Promise<Guide | null> {
    const sb = getSB();
    const data = await fetchGuideRow(sb, id);
    if (!data) return null;

    const rawId = data.user_id ?? data.id ?? id;
    const canonicalId = typeof rawId === "string" ? rawId : String(rawId);
    const guideIdentifier =
        data.id !== undefined && data.id !== null
            ? (typeof data.id === "string" ? data.id : String(data.id))
            : canonicalId;
    const userIdentifier =
        data.user_id !== undefined && data.user_id !== null
            ? (typeof data.user_id === "string" ? data.user_id : String(data.user_id))
            : canonicalId;

    const [onboarding, dealsClosedRate] = await Promise.all([
        loadOnboardingData(sb, {
            guideId: guideIdentifier,
            userId: userIdentifier,
        }),
        calculateDealsClosedRate(sb, guideIdentifier),
    ]);

    return {
        id: canonicalId,
        name: data.name ?? "",
        email: data.email ?? "",
        phone: data.phone ?? null,
        status: (data.status ?? "pending") as GuideStatus,
        verificationLevel: undefined,
        backgroundChecked: normalizeBoolean(data.backgroundChecked),
        connectedSeekers: Number(data.connected_seekers ?? 0),
        income: Number(data.income ?? 0),
        joinedAt: data.joined_at ?? new Date().toISOString(),
        dealsClosedRate,
        platformFeePercentage: data.platform_fee_percentage != null ? Number(data.platform_fee_percentage) : null,
        region: data.region ?? undefined,
        specialties: Array.isArray(data.specialties) ? data.specialties : undefined,
        onboarding,
    };
}

// verification level column was removed in the latest schema rebuild.
export async function updateGuideVerificationLevel(_id: string, _next: "none" | "partial" | "full"): Promise<void> {
    throw new Error("Verification level is no longer supported in the schema.");
}

export async function updateGuideBackgroundChecked(id: string, backgroundChecked: boolean | null): Promise<void> {
    const sb = getSB();
    const { error } = await sb
        .from("profiles")
        .update({ backgroundChecked: backgroundChecked })
        .eq("id", id);

    if (error) throw error;
}

export async function updateGuidePlatformFee(id: string, percentage: number | null): Promise<void> {
    const sb = getSB();
    const { error } = await sb
        .from("profiles")
        .update({ platform_fee_percentage: percentage })
        .eq("id", id);

    if (error) throw error;
}

const CONNECTED_ORDER_STATUSES = [
    "paid_pending_delivery",
    "delivered",
];

const normalizeInstallmentStatus = (status?: string | null) => {
    if (!status) return "scheduled";
    const normalized = status.toLowerCase();
    if (["paid", "succeeded", "completed"].includes(normalized)) return "paid";
    if (["scheduled", "pending", "pending_payment", "requires_payment_method", "active"].includes(normalized)) {
        return "scheduled";
    }
    if (["processing", "in_progress"].includes(normalized)) return "processing";
    if (["failed", "canceled", "cancelled", "overdue"].includes(normalized)) return "failed";
    return normalized;
};

export async function getGuideStats() {
    const sb = getSB();

    // Get total guide count and status breakdown
    const { data: guides, error: guidesError, count: totalCount } = await sb
        .from("guides")
        .select("status", { count: "exact" });

    if (guidesError) throw guidesError;

    const total = totalCount ?? 0;
    const active = guides?.filter(g => g.status === "active").length ?? 0;

    // Calculate total connections from active matches
    const { data: matchesData, error: matchesError } = await sb
        .from("matches")
        .select("guide_id, seeker_id")
        .eq("status", "active");

    if (matchesError) {
        console.warn("Failed to load matches for stats:", matchesError);
    }

    // Count unique seeker connections across all guides
    const uniqueSeekers = new Set<string>();
    (matchesData ?? []).forEach((match) => {
        if (match.seeker_id) {
            uniqueSeekers.add(match.seeker_id);
        }
    });
    const connections = uniqueSeekers.size;

    // Calculate total revenue from orders and installments
    const { data: ordersData, error: ordersError } = await sb
        .from("orders")
        .select("id, guide_id, status, total_cents")
        .in("status", CONNECTED_ORDER_STATUSES);

    if (ordersError) {
        console.warn("Failed to load orders for stats:", ordersError);
    }

    const orderIds = Array.from(
        new Set((ordersData ?? []).map((order) => order.id).filter((id): id is string => !!id))
    );

    let installmentsData: any[] = [];
    if (orderIds.length > 0) {
        const { data, error: installmentsError } = await sb
            .from("order_installments")
            .select("order_id, amount_cents, status")
            .in("order_id", orderIds);

        if (installmentsError) {
            console.warn("Failed to load installments for stats:", installmentsError);
        } else {
            installmentsData = data ?? [];
        }
    }

    // Calculate paid amounts
    const installmentsTotalMap = new Map<string, number>();
    const installmentsPaidMap = new Map<string, number>();
    installmentsData.forEach((inst) => {
        if (!inst.order_id) return;
        const totalPrev = installmentsTotalMap.get(inst.order_id) ?? 0;
        installmentsTotalMap.set(inst.order_id, totalPrev + (inst.amount_cents ?? 0));

        if (normalizeInstallmentStatus(inst.status) === "paid") {
            const paidPrev = installmentsPaidMap.get(inst.order_id) ?? 0;
            installmentsPaidMap.set(inst.order_id, paidPrev + (inst.amount_cents ?? 0));
        }
    });

    let totalRevenueCents = 0;
    (ordersData ?? []).forEach((order) => {
        const totalCents = Number(order.total_cents ?? 0);
        const installmentsTotal = installmentsTotalMap.get(order.id) ?? 0;
        const depositCents = Math.max(totalCents - installmentsTotal, 0);
        const paidInstallments = installmentsPaidMap.get(order.id) ?? 0;
        const depositPaid = CONNECTED_ORDER_STATUSES.includes(order.status);
        const earnedCents = (depositPaid ? depositCents : 0) + paidInstallments;
        totalRevenueCents += earnedCents;
    });

    const revenue = Number((totalRevenueCents / 100).toFixed(2));

    return {
        total,
        approved: active, // Return active count but keep "approved" key for compatibility
        connections,
        revenue,
    };
}












